<template>
  <div class="biji-container">
    <!-- 卷首 · 题签 -->
    <header class="scroll-header">
      <div class="header-inner">
        <h1 class="header-title">拾笔成记 · 积字成笺</h1>
        <span class="header-seal">笔</span>
      </div>
      <p class="header-sub">
        一笺一世界 · 一笔一浮生 —— jmdvirus
        <router-link to="/bijimng" class="game-link">笔记管理</router-link>
      </p>
    </header>

    <!-- 检索 · 筛选 -->
    <div class="filter-section">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索标题 / 副标题 / 作者 / 内容"
        prefix-icon="el-icon-search"
        clearable
        @input="onSearch"
        class="search-input"
      />
      <el-select
        v-model="selectedCategory"
        placeholder="按标题筛选"
        @change="onCategoryChange"
        clearable
        class="filter-select"
      >
        <el-option label="全部笔记" value=""></el-option>
        <el-option v-for="category in categories" :key="category" :label="category" :value="category"></el-option>
      </el-select>
      <div class="sort-toggle">
        <button
          v-for="opt in sortOptions"
          :key="opt.value"
          :class="{ 'is-active': sortOrder === opt.value }"
          @click="sortOrder = opt.value"
        >{{ opt.label }}</button>
      </div>
      <span class="stats-text">库中共 {{ total }} 篇</span>
    </div>

    <!-- 笔记 · 竖向时间轴 -->
    <div class="timeline" v-if="displayItems.length">
      <section class="year-group" v-for="group in timelineMonthGroups" :key="group.year">
        <!-- 年份题头 -->
        <div class="year-head">
          <span class="year-cn">{{ group.cn }}</span>
          <span class="year-num">{{ group.year }}</span>
          <i class="year-rule"></i>
        </div>

        <!-- 该年笔记：按月分节，月头为目录锚点 -->
        <div class="year-items">
          <div class="month-group" v-for="month in group.months" :key="month.key">
            <div class="month-head" :id="month.anchor">
              <span class="month-label">{{ monthToCn(month.month) }}月</span>
            </div>
            <article
              class="timeline-item"
              v-for="(item, idx) in month.items"
              :key="item.title + '-' + item.createTime"
              :style="{ animationDelay: (Math.min(idx, 8) * 70) + 'ms' }"
            >
              <div class="item-side">
                <span class="item-date">{{ formatDay(item) }}</span>
                <span class="item-time">{{ formatClock(item) }}</span>
              </div>
              <div class="biji-card" @click="openDetail(item)">
                <h3 class="biji-title">{{ item.title }}</h3>
                <p class="biji-subtitle" v-if="item.subtitle">{{ item.subtitle }}</p>
                <p class="biji-author">
                  <span class="author-mark">〔</span>{{ item.author || '我' }}<span class="author-mark">〕</span>
                </p>
                <div class="biji-content">
                  <p v-for="(line, lineIdx) in formatContent(item.content).slice(0, 4)" :key="lineIdx" class="biji-line">
                    {{ line }}
                  </p>
                </div>
                <div class="card-footer">
                  <span class="biji-time">{{ item.createtime }}</span>
                  <span class="view-detail">展开全篇<i class="el-icon-arrow-right"></i></span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>

    <!-- 悬浮目录：按 年/月 定位 -->
    <div class="toc-panel" v-if="tocVisible && displayItems.length">
      <div class="toc-head">
        <span class="toc-title">目录</span>
        <button class="toc-close" @click="tocVisible = false"><i class="el-icon-close"></i></button>
      </div>
      <div class="toc-body" ref="tocBody">
        <div class="toc-year" v-for="g in timelineMonthGroups" :key="g.year">
          <div class="toc-year-label">
            <span class="toc-year-num">{{ g.year }}</span>
            <span class="toc-year-cn">{{ g.cn }}</span>
          </div>
          <a
            href="javascript:;"
            class="toc-month"
            v-for="m in g.months"
            :key="m.key"
            :class="{ 'is-active': activeAnchor === m.anchor }"
            @click="scrollToAnchor(m.anchor)"
          >{{ m.month ? monthToCn(m.month) + '月' : '未署' }}<i>{{ m.items.length }}</i></a>
        </div>
      </div>
    </div>
    <button class="toc-fab" v-else-if="displayItems.length" @click="tocVisible = true">目录</button>

    <!-- 空态 -->
    <div class="empty-tip" v-else-if="!isLoading">
      <span class="empty-seal">寻</span>
      <p>笺中未见此篇</p>
    </div>

    <!-- 展卷 · 加载更多 -->
    <div class="load-more-section" v-if="hasMore">
      <button class="load-more-btn" :class="{ 'is-loading': isLoading }" @click="queryBiji">
        {{ isLoading ? '展笺中…' : '再展一笺' }}
      </button>
    </div>

    <!-- 详情 · 全篇 -->
    <el-dialog
      :visible.sync="dialogVisible"
      width="680px"
      class="biji-dialog"
      center
      :show-close="false"
    >
      <div v-if="selectedItem" class="dialog-content">
        <button class="dialog-close" @click="dialogVisible = false"><i class="el-icon-close"></i></button>
        <span class="dialog-seal">{{ (selectedItem.author || '我')[0] }}</span>
        <h3 class="dialog-title">{{ selectedItem.title }}</h3>
        <p class="dialog-subtitle" v-if="selectedItem.subtitle">{{ selectedItem.subtitle }}</p>
        <p class="dialog-author">〔 {{ selectedItem.author || '我' }} 〕</p>
        <div class="dialog-divider"><i></i></div>
        <div class="dialog-biji">
          <p v-for="(line, idx) in formatContent(selectedItem.content)" :key="idx" class="dialog-line">
            {{ line }}
          </p>
        </div>
        <div class="dialog-divider"><i></i></div>
        <p class="dialog-time">收录于 {{ selectedItem.createtime }}</p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Vue from "vue";
import {UseUrl} from "../../utils/config";
import * as utils from "../../utils/utils"

const CN_DIGITS = ["〇", "一", "二", "三", "四", "五", "六", "七", "八", "九"];

export default {
  name: "biji",
  data() {
    return {
      items: [],
      displayItems: [],
      index: 0,
      limit: 10,
      // 数据库总数（每次响应覆盖；首屏即获知「库里共多少」）
      total: 0,
      selectedCategory: "",
      categories: [],
      isLoading: false,
      searchKeyword: "",
      sortOrder: "desc",
      querySeq: 0,
      sortOptions: [
        { label: "新→旧", value: "desc" },
        { label: "旧→新", value: "asc" }
      ],
      dialogVisible: false,
      selectedItem: null,
      hasMore: true,
      // 宽屏默认展开；窄屏默认收起，避免遮挡内容
      tocVisible: window.innerWidth >= 1300,
      activeAnchor: "",
      scrollTicking: false,
      me: {}
    }
  },
  computed: {
    isAdmin() {
      return String(this.me?.role || '') === 'admin'
    },
    // 按收录时间排序 → 按年份分组，形成时间轴（顺序跟随排序选择）
    timelineGroups: function() {
      const dir = this.sortOrder === "asc" ? 1 : -1;
      const sorted = [...this.displayItems].sort((a, b) => {
        return ((a.createTime || 0) - (b.createTime || 0)) * dir;
      });
      const groups = [];
      const bucket = {};
      sorted.forEach(item => {
        const year = item.createTime
          ? new Date(item.createTime * 1000).getFullYear() + ""
          : "未知";
        if (!bucket[year]) {
          bucket[year] = { year: year, items: [] };
          groups.push(bucket[year]);
        }
        bucket[year].items.push(item);
      });
      return groups;
    },
    // 在年份分组基础上再按月分节，供时间轴月锚点与悬浮目录使用
    timelineMonthGroups: function() {
      return this.timelineGroups.map(group => {
        const yearTag = group.year === "未知" ? "na" : group.year;
        const months = [];
        const bucket = {};
        group.items.forEach(item => {
          const d = item.createTime ? new Date(item.createTime * 1000) : null;
          const month = d ? d.getMonth() + 1 : 0;
          const key = d ? (d.getFullYear() + "-" + (month < 10 ? "0" + month : month)) : "unknown";
          if (!bucket[key]) {
            bucket[key] = {
              key: key,
              month: month,
              anchor: "bj-" + yearTag + "-" + key,
              items: []
            };
            months.push(bucket[key]);
          }
          bucket[key].items.push(item);
        });
        return {
          year: group.year,
          cn: this.yearToCn(group.year),
          count: group.items.length,
          months: months
        };
      });
    }
  },
  watch: {
    sortOrder: function() {
      this.resetQuery();
    },
    items: {
      handler(newItems) {
        this.displayItems = newItems;
      },
      deep: false
    }
  },
  methods: {
    queryBiji: function() {
      if (this.isLoading) {
        console.log("正在加载中，跳过重复请求");
        return;
      }

      this.isLoading = true;
      const seq = this.querySeq;
      const params = new URLSearchParams();
      params.append('start', this.index);
      params.append('limit', this.limit);
      // 服务端按 createtime 排序：desc=新→旧，asc=旧→新
      params.append('order', this.sortOrder);
      // 服务端分类过滤：选中分类时只拉该标题
      if (this.selectedCategory) {
        params.append('title', this.selectedCategory);
      }
      // 服务端关键词搜索：覆盖整库（标题 / 副标题 / 作者 / 正文）
      const kw = (this.searchKeyword || '').trim();
      if (kw) {
        params.append('keyword', kw);
      }
      var that = this;
      console.log("biji start to query ", this.index, " limit ", this.limit, " order ", this.sortOrder, " title ", this.selectedCategory, " keyword ", kw);

      Vue.axios.post(UseUrl + "/biji/BijiFind", params).then((response) => {
        // 排序/分类切换后的过期响应，丢弃并按新条件重拉
        if (seq !== that.querySeq) {
          that.isLoading = false;
          that.queryBiji();
          return;
        }

        var curItem = response.data.Item;
        // 数据库总数：每次响应覆盖（首屏即获知「库里共多少」）
        if (typeof response.data.total === 'number') {
          that.total = response.data.total;
        }

        if (!curItem || curItem.length === 0) {
          that.hasMore = false;
          that.isLoading = false;
          return;
        }

        for (var i = 0; i < curItem.length; i++)
        {
          curItem[i]["createtime"] = utils.timestampToTime(curItem[i]["createTime"]);
          that.items.push(curItem[i]);
        }
        that.index += curItem.length;
        that.isLoading = false;
        that.filterDisplayItems();

        if (curItem.length < that.limit) {
          that.hasMore = false;
        }
        console.log("biji index: ", that.index, ", cur length ", curItem.length, ", all items ", that.items.length);
      }).catch((error => {
        console.log(error);
        that.isLoading = false;
      }))
    },
    // 重置分页，按当前排序/分类从服务端重新取首页
    resetQuery: function() {
      this.querySeq++;
      this.items = [];
      this.displayItems = [];
      this.index = 0;
      this.hasMore = true;
      // 若有在途请求，由过期响应分支自动重拉；否则直接拉取
      if (!this.isLoading) {
        this.queryBiji();
      }
    },
    // 一次拉取全库分类（后端 Distinct 去重），不再从分页数据累积
    queryCategories: function() {
      var that = this;
      Vue.axios.post(UseUrl + "/biji/categories").then((response) => {
        that.categories = response.data.categories || [];
        console.log("biji categories: ", that.categories.length);
      }).catch((error) => {
        console.log(error);
      })
    },
    onCategoryChange: function() {
      // 分类筛选走服务端：同规则（排序+分页）重新拉取
      this.resetQuery();
    },
    onSearch: function() {
      // 关键词搜索走后端整库匹配：防抖后按新条件从服务端重取
      if (this._searchTimer) {
        clearTimeout(this._searchTimer);
      }
      this._searchTimer = setTimeout(() => {
        this.resetQuery();
      }, 300);
    },
    filterDisplayItems: function() {
      // 关键词与分类均由服务端完成，这里仅同步展示列表
      this.displayItems = [...this.items];
    },
    formatContent: function(content) {
      if (!content) return [];
      return content.split(/[\n\r]/).filter(line => line.trim() !== '');
    },
    openDetail: function(item) {
      this.selectedItem = item;
      this.dialogVisible = true;
    },
    // 2018 → 二〇一八
    yearToCn: function(year) {
      if (year === "未知") return "未署年月";
      return String(year).split("").map(ch => CN_DIGITS[Number(ch)] || ch).join("");
    },
    // 11 → 十一
    monthToCn: function(month) {
      if (!month) return "";
      if (month === 10) return "十";
      if (month === 11) return "十一";
      if (month === 12) return "十二";
      return CN_DIGITS[month];
    },
    // 悬浮目录：平滑定位到月份锚点
    scrollToAnchor: function(anchor) {
      const el = document.getElementById(anchor);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      this.activeAnchor = anchor;
      // 目录面板内跟随
      this.$nextTick(() => {
        const body = this.$refs.tocBody;
        const active = body && body.querySelector(".toc-month.is-active");
        if (body && active) {
          body.scrollTop = Math.max(0, active.offsetTop - body.offsetTop - body.clientHeight / 2);
        }
      });
    },
    // 滚动时高亮当前所在月份
    onScroll: function() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.scrollTicking = false;
        const heads = document.querySelectorAll(".month-head");
        let current = "";
        for (let i = 0; i < heads.length; i++) {
          if (heads[i].getBoundingClientRect().top <= 130) {
            current = heads[i].id;
          } else {
            break;
          }
        }
        this.activeAnchor = current;
      });
    },
    formatDay: function(item) {
      if (!item.createTime) return "——";
      const d = new Date(item.createTime * 1000);
      const m = d.getMonth() + 1;
      const day = d.getDate();
      return (m < 10 ? "0" + m : m) + "·" + (day < 10 ? "0" + day : day);
    },
    formatClock: function(item) {
      if (!item.createTime) return "";
      const d = new Date(item.createTime * 1000);
      const h = d.getHours();
      const mi = d.getMinutes();
      return (h < 10 ? "0" + h : h) + ":" + (mi < 10 ? "0" + mi : mi);
    },
    refreshMe() {
      try {
        this.me = JSON.parse(localStorage.getItem('shicigame_user') || '{}') || {}
      } catch (e) {
        this.me = {}
      }
    }
  },
  mounted() {
    this.refreshMe();
    window.addEventListener('shicigame-auth-changed', this.refreshMe, false);
    this.queryCategories();
    this.queryBiji();
    window.addEventListener("scroll", this.onScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener('shicigame-auth-changed', this.refreshMe, false);
  },
  beforeCreate () {
    this.index = 0;
    this.limit = 10;
    this.items = [];
    this.displayItems = [];
    this.total = 0;
    this.selectedCategory = "";
    this.categories = [];
    this.isLoading = false;
  }
}
</script>

<style scoped>
/* ============ 基调：宣纸 · 水墨 · 朱砂（与 shici 同调，仅个别 icon 略小） ============ */
.biji-container {
  --paper: #f5f1e5;
  --paper-card: #fdfaf1;
  --ink: #26221a;
  --ink-soft: #5c554a;
  --ink-faint: #99917f;
  --hairline: #ddd3ba;
  --cinnabar: #b13a26;
  --cinnabar-deep: #8e2c1c;
  --font-display: "Noto Serif SC", "Source Han Serif SC", "Songti SC", STSong, SimSun, serif;
  --font-kai: "Kaiti SC", KaiTi, STKaiti, "Noto Serif SC", serif;

  max-width: 1060px;
  margin: 0 auto;
  /* 横向内边距收紧，让纸面尽量贴近容器边缘，缩小两侧白条 */
  padding: 28px 8px 60px;

  color: var(--ink);
  font-family: var(--font-display);
  /* 容器不再画边界与投影，只做纸面叠加。底色交给 #app 的整页宣纸，避免容器与容器外的色差形成「白边」 */
  background-color: transparent;
  background-image:
    radial-gradient(1200px 500px at 20% -10%, rgba(255, 252, 240, 0.9), transparent 60%),
    radial-gradient(900px 420px at 100% 0%, rgba(233, 224, 200, 0.55), transparent 55%),
    radial-gradient(700px 360px at 50% 110%, rgba(177, 58, 38, 0.05), transparent 60%),
    repeating-linear-gradient(0deg, rgba(153, 145, 127, 0.04) 0 1px, transparent 1px 7px),
    repeating-linear-gradient(90deg, rgba(153, 145, 127, 0.03) 0 1px, transparent 1px 11px);
}

/* ============ 卷首题签 ============ */
.scroll-header {
  text-align: center;
  padding: 26px 0 8px;
  margin-bottom: 18px;
}

.header-inner {
  display: inline-flex;
  align-items: flex-start;
  gap: 14px;
  position: relative;
}

.header-title {
  margin: 0;
  font-size: 36px;
  font-weight: 700;
  letter-spacing: 8px;
  text-indent: 8px;
  color: var(--ink);
}

/* 题名旁的朱砂印 */
.header-seal {
  align-self: flex-end;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cinnabar);
  color: #fdf6ec;
  font-size: 19px;
  font-family: var(--font-kai);
  border-radius: 6px;
  box-shadow: inset 0 0 0 2px rgba(253, 246, 236, 0.35), 0 2px 6px rgba(177, 58, 38, 0.35);
  transform: rotate(-4deg);
}

.header-sub {
  margin: 10px 0 0;
  color: var(--ink-faint);
  font-size: 14px;
  letter-spacing: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
}

.game-link {
  color: var(--cinnabar);
  text-decoration: none;
  padding: 3px 12px;
  border: 1px solid rgba(177, 58, 38, 0.45);
  border-radius: 2px;
  letter-spacing: 2px;
  font-size: 13px;
  transition: all 0.25s ease;
}

.game-link:hover {
  background: var(--cinnabar);
  color: #fdf6ec;
}

/* ============ 检索区：搜索 + 筛选同行，与内容同宽 ============ */
.filter-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 14px 18px;
  margin: 0 auto 10px;
  max-width: 1060px;
  background: rgba(253, 250, 241, 0.8);
  border: 1px solid var(--hairline);
  border-radius: 4px;
}

.search-input {
  flex: 1;
  min-width: 0;
}

.filter-select {
  width: 200px;
}

/* 排序切换：朱砂描边小开关 */
.sort-toggle {
  display: inline-flex;
  border: 1px solid rgba(177, 58, 38, 0.45);
  border-radius: 3px;
  overflow: hidden;
}

.sort-toggle button {
  font-family: var(--font-display);
  font-size: 13px;
  letter-spacing: 2px;
  color: var(--cinnabar);
  background: transparent;
  border: none;
  padding: 7px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sort-toggle button + button {
  border-left: 1px solid rgba(177, 58, 38, 0.35);
}

.sort-toggle button:hover {
  background: rgba(177, 58, 38, 0.08);
}

.sort-toggle button.is-active {
  background: var(--cinnabar);
  color: #fdf6ec;
}

.stats-text {
  margin-left: auto;
  color: var(--ink-faint);
  font-size: 13px;
  letter-spacing: 2px;
  white-space: nowrap;
}

/* ============ 时间轴 ============ */
.timeline {
  position: relative;
  margin-top: 26px;
}

/* 年份题头：竖排汉数年号 + 横线 */
.year-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 42px 0 10px;
}

.year-group:first-child .year-head {
  margin-top: 0;
}

.year-cn {
  writing-mode: vertical-rl;
  font-family: var(--font-kai);
  font-size: 26px;
  letter-spacing: 8px;
  color: var(--cinnabar);
  line-height: 1;
  padding: 4px 2px;
  border: 1px solid rgba(177, 58, 38, 0.4);
  border-radius: 2px;
  background: rgba(177, 58, 38, 0.05);
}

.year-num {
  font-size: 30px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: 2px;
}

.year-rule {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, var(--hairline), transparent);
}

/* 轴身 */
.year-items {
  position: relative;
  padding-left: 128px;
}

/* 轴线：贯穿的淡墨线，距卡片左缘留出明显空隙 */
.year-items::before {
  content: "";
  position: absolute;
  left: 110px;
  top: 4px;
  bottom: 4px;
  width: 1px;
  background: linear-gradient(180deg, var(--hairline) 0%, #cfc4a8 50%, var(--hairline) 100%);
}

.timeline-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  margin-bottom: 26px;
  animation: item-rise 0.55s cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

@keyframes item-rise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 时间侧栏：日期 + 节点（与轴线一起向左整体平移，保持间距比例） */
.item-side {
  position: absolute;
  left: -134px;
  top: 2px;
  width: 104px;
  padding-right: 36px;
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.item-date {
  font-size: 16px;
  font-weight: 600;
  color: var(--ink-soft);
  letter-spacing: 1px;
  line-height: 1.2;
}

.item-time {
  font-size: 11px;
  color: var(--ink-faint);
  margin-top: 6px;
  letter-spacing: 1px;
}

/* ============ 笔记卡 ============ */
.biji-card {
  position: relative;
  flex: 1;
  min-width: 0;
  background: var(--paper-card);
  border: 1px solid var(--hairline);
  border-radius: 4px;
  padding: 22px 58px 16px 26px;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  box-shadow: 2px 3px 0 rgba(38, 34, 26, 0.04);
}

.biji-card:hover {
  transform: translate(3px, -3px);
  border-color: rgba(177, 58, 38, 0.5);
  box-shadow: 6px 8px 0 rgba(177, 58, 38, 0.1);
}

.biji-title {
  margin: 0 0 4px;
  font-size: 21px;
  font-weight: 700;
  letter-spacing: 3px;
  color: var(--ink);
}

.biji-subtitle {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--ink-faint);
  letter-spacing: 2px;
  font-style: italic;
}

.biji-author {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--ink-faint);
  letter-spacing: 2px;
}

.author-mark {
  color: rgba(177, 58, 38, 0.6);
}

.biji-content {
  position: relative;
  padding: 10px 0 12px;
  border-top: 1px dashed var(--hairline);
  max-height: 132px;
  overflow: hidden;
}

.biji-content::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 42px;
  background: linear-gradient(transparent, var(--paper-card));
}

.biji-line {
  margin: 4px 0;
  font-family: var(--font-kai);
  font-size: 16px;
  color: var(--ink-soft);
  text-align: center;
  letter-spacing: 2px;
  line-height: 1.9;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(221, 211, 186, 0.6);
  padding-top: 10px;
}

.biji-time {
  font-size: 12px;
  color: var(--ink-faint);
  letter-spacing: 1px;
}

.view-detail {
  font-size: 13px;
  color: var(--cinnabar);
  letter-spacing: 2px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  opacity: 0.75;
  transition: opacity 0.2s, gap 0.2s;
}

.biji-card:hover .view-detail {
  opacity: 1;
  gap: 8px;
}

/* ============ 空态 ============ */
.empty-tip {
  text-align: center;
  padding: 70px 0 40px;
  color: var(--ink-faint);
  letter-spacing: 4px;
}

.empty-seal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: 14px;
  font-family: var(--font-kai);
  font-size: 24px;
  color: rgba(177, 58, 38, 0.7);
  border: 2px solid rgba(177, 58, 38, 0.45);
  border-radius: 6px;
  transform: rotate(-4deg);
}

/* ============ 加载更多 ============ */
.load-more-section {
  text-align: center;
  padding: 26px 0 10px;
}

.load-more-btn {
  font-family: var(--font-display);
  font-size: 15px;
  letter-spacing: 6px;
  text-indent: 6px;
  color: var(--cinnabar);
  background: transparent;
  border: 1px solid rgba(177, 58, 38, 0.5);
  border-radius: 3px;
  padding: 10px 44px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.load-more-btn:hover {
  background: var(--cinnabar);
  color: #fdf6ec;
  box-shadow: 0 4px 12px rgba(177, 58, 38, 0.3);
}

.load-more-btn.is-loading {
  opacity: 0.6;
  cursor: wait;
}

/* ============ 详情弹窗 ============ */
.biji-dialog >>> .el-dialog {
  border-radius: 6px;
  overflow: hidden;
  background: var(--paper-card);
  border: 1px solid var(--hairline);
  box-shadow: 0 18px 50px rgba(38, 34, 26, 0.25);
}

.biji-dialog >>> .el-dialog__header {
  display: none;
}

.biji-dialog >>> .el-dialog__body {
  padding: 0;
}

.dialog-content {
  position: relative;
  padding: 40px 46px 30px;
  background:
    radial-gradient(500px 200px at 50% 0%, rgba(233, 224, 200, 0.4), transparent 70%),
    var(--paper-card);
}

.dialog-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: var(--ink-faint);
  font-size: 18px;
  cursor: pointer;
  transition: color 0.2s;
}

.dialog-close:hover {
  color: var(--cinnabar);
}

.dialog-seal {
  position: absolute;
  top: 36px;
  right: 40px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cinnabar);
  color: #fdf6ec;
  font-family: var(--font-kai);
  font-size: 22px;
  border-radius: 5px;
  box-shadow: inset 0 0 0 2px rgba(253, 246, 236, 0.35);
  transform: rotate(4deg);
}

.dialog-title {
  margin: 0;
  text-align: center;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 6px;
  color: var(--ink);
}

.dialog-subtitle {
  margin: 6px 0 0;
  text-align: center;
  font-size: 13px;
  color: var(--ink-faint);
  letter-spacing: 2px;
  font-style: italic;
}

.dialog-author {
  margin: 10px 0 0;
  text-align: center;
  font-size: 14px;
  color: var(--ink-faint);
  letter-spacing: 3px;
}

.dialog-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
}

.dialog-divider::before,
.dialog-divider::after {
  content: "";
  width: 90px;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--hairline));
}

.dialog-divider::after {
  background: linear-gradient(90deg, var(--hairline), transparent);
}

.dialog-divider i {
  width: 6px;
  height: 6px;
  background: var(--cinnabar);
  transform: rotate(45deg);
  margin: 0 10px;
}

.dialog-biji {
  padding: 4px 0;
}

.dialog-line {
  margin: 7px 0;
  font-family: var(--font-kai);
  font-size: 18px;
  color: var(--ink-soft);
  text-align: center;
  letter-spacing: 3px;
  line-height: 2;
}

.dialog-time {
  margin: 0;
  text-align: center;
  font-size: 13px;
  color: var(--ink-faint);
  letter-spacing: 2px;
}

/* 月份锚点头 */
.month-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 20px 0 14px;
  scroll-margin-top: 18px;
}

.month-group:first-child .month-head {
  margin-top: 6px;
}

.month-label {
  font-family: var(--font-kai);
  font-size: 16px;
  color: var(--cinnabar);
  letter-spacing: 3px;
}

.month-head::after {
  content: "";
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(177, 58, 38, 0.25), transparent);
}

/* ============ 悬浮目录 ============ */
.toc-panel {
  position: fixed;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  width: 148px;
  background: rgba(253, 250, 241, 0.2);
  border: 1px solid var(--hairline);
  border-radius: 6px;
  box-shadow: 3px 4px 0 rgba(38, 34, 26, 0.06);
  z-index: 60;
  overflow: hidden;
}

.toc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px 8px 14px;
  border-bottom: 1px solid rgba(221, 211, 186, 0.7);
}

.toc-title {
  font-family: var(--font-kai);
  font-size: 15px;
  color: var(--ink);
  letter-spacing: 4px;
}

.toc-close {
  border: none;
  background: transparent;
  color: var(--ink-faint);
  font-size: 14px;
  cursor: pointer;
  padding: 2px;
  transition: color 0.2s;
}

.toc-close:hover {
  color: var(--cinnabar);
}

.toc-body {
  position: relative;
  max-height: 62vh;
  overflow-y: auto;
  padding: 8px 6px 10px;
}

.toc-body::-webkit-scrollbar {
  width: 4px;
}

.toc-body::-webkit-scrollbar-thumb {
  background: rgba(177, 58, 38, 0.3);
  border-radius: 2px;
}

.toc-year {
  margin-bottom: 8px;
}

.toc-year-label {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 4px 8px 3px;
}

.toc-year-num {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: 1px;
}

.toc-year-cn {
  font-family: var(--font-kai);
  font-size: 11px;
  color: var(--ink-faint);
  letter-spacing: 2px;
}

.toc-month {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 10px 4px 18px;
  font-size: 13px;
  color: var(--ink-soft);
  text-decoration: none;
  letter-spacing: 2px;
  border-left: 2px solid transparent;
  border-radius: 2px;
  transition: all 0.15s ease;
}

.toc-month i {
  font-style: normal;
  font-size: 11px;
  color: var(--ink-faint);
}

.toc-month:hover {
  color: var(--cinnabar);
  background: rgba(177, 58, 38, 0.06);
}

.toc-month.is-active {
  color: var(--cinnabar);
  border-left-color: var(--cinnabar);
  background: rgba(177, 58, 38, 0.08);
}

.toc-month.is-active i {
  color: var(--cinnabar);
}

/* 目录收起后的悬浮按钮 */
.toc-fab {
  position: fixed;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  writing-mode: vertical-rl;
  font-family: var(--font-kai);
  font-size: 14px;
  letter-spacing: 6px;
  color: var(--cinnabar);
  background: rgba(253, 250, 241, 0.2);
  border: 1px solid rgba(177, 58, 38, 0.45);
  border-radius: 14px;
  padding: 14px 7px;
  cursor: pointer;
  z-index: 60;
  transition: all 0.2s ease;
}

.toc-fab:hover {
  background: var(--cinnabar);
  color: #fdf6ec;
}

/* 窄屏：半透明磨砂悬浮在内容之上，不隐藏 */
@media (max-width: 1299px) {
  .toc-panel {
    right: 10px;
    width: 128px;
    background: rgba(253, 250, 241, 0.2);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    box-shadow: 0 4px 14px rgba(38, 34, 26, 0.12);
  }

  .toc-body {
    max-height: 56vh;
  }

  .toc-month {
    padding: 4px 8px 4px 14px;
  }

  .toc-fab {
    right: 10px;
    background: rgba(253, 250, 241, 0.2);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }
}

/* ============ 响应式 ============ */
@media (max-width: 640px) {
  .header-title {
    font-size: 24px;
    letter-spacing: 5px;
    text-indent: 5px;
  }

  .header-sub {
    letter-spacing: 2px;
    flex-direction: column;
    gap: 10px;
  }

  .year-items {
    padding-left: 88px;
  }

  .year-items::before {
    left: 76px;
  }

  .item-side {
    left: -94px;
    width: 72px;
    padding-right: 26px;
  }

  .item-date {
    font-size: 13px;
  }

  .item-time {
    display: none;
  }

  .biji-card {
    padding: 16px 46px 12px 18px;
  }

  .biji-title {
    font-size: 18px;
  }

  .dialog-content {
    padding: 30px 22px 24px;
  }

  .dialog-seal {
    right: 18px;
    top: 26px;
  }
}
</style>